import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Button from "../Button/Button";
import styles from "./AdditionalDetailsDialog.module.css";

type AdditionalDetailsDialogProps = {
  open: boolean;
  value: string;
  onChange: (val: string) => void;
  onClose: () => void;
  onSave: () => void;
};

export default function AdditionalDetailsDialog({
  open,
  value,
  onChange,
  onClose,
  onSave,
}: AdditionalDetailsDialogProps) {
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;

    const t = setTimeout(() => textRef.current?.focus(), 50);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Prevent background scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles["dialog-overlay"]}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={styles["dialog"]}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detailsDialogTitle"
      >
        <div className={styles["dialog-header"]}>
          <p id="detailsDialogTitle">Additional details</p>
        </div>
        <div className={styles["dialog-body"]}>
          <p className={styles["dialog-body-title"]}>
            Please provide some more information about your issue to make it
            easier for us to help you.{" "}
          </p>
          <div className={styles["dialog-body-text-container"]}>
            <textarea
              ref={textRef}
              className={styles["dialog-textarea"]}
              rows={8}
              maxLength={2000}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Type details here..."
            />
          </div>
        </div>
        <div className={styles["dialog-footer"]}>
          <Button variant="transparent" text="Close" onClick={onClose} />
          <Button variant="secondary" text="Save" onClick={onSave} />
        </div>
      </div>
    </div>,
    document.body
  );
}
